/**
 *  @swagger
 *  definitions:
 *    RegattaCourseParams:
 *      type: object
 *      properties:
 *        RegattaCourseId:
 *          description: ID of the associated RegattaCourse
 *          type: string
 *        Public:
 *          description: Public of the RegattaCourseRace
 *          type: boolean
 *        Name:
 *          description: Name of the RegattaCourseRace
 *          type: string
 *          type: boolean
 *        UpadateInterval:
 *          description: Upadate Interval of the RegattaCourseRace
 *          type: integer
 *        StartDate:
 *          description: Start Date of the RegattaCourseRace
 *          type: string
 *          format: date-time
 *        EndDate:
 *          description: End Date of the RegattaCourseRace
 *          type: string
 *          format: date-time
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseCreateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseParams'
 *        - type: object
 *          required:
 *            - RegattaId
 *            - Public
 *            - Name
 *            - UpadateInterval
 *            - StartDate
 *            - EndDate
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseQueryParams:
 *      allOf:
 *        - type: object
 *          properties:
 *            id:
 *              description: ID of the RegattaCourse
 *              type: string
 *        - $ref: '#/definitions/RegattaCourseParams'
 *
 *  @swagger
 *  definitions:
 *    RegattaCourseUpdateParams:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseQueryParams'
 *        - type: object
 *          required:
 *            - id
 *
 *  @swagger
 *  definitions:
 *    RegattaCourse:
 *      allOf:
 *        - $ref: '#/definitions/RegattaCourseCreateParams'
 *        - $ref: '#/definitions/RegattaCourseUpdateParams'
 *        - $ref: '#/definitions/Timestamps'
 *
 *  @swagger
 *  components:
 *    schemas:
 *      RegattaCourse:
 *        allOf:
 *          - $ref: '#/definitions/RegattaCourseRace'
 *              RegattaCourse:
 *                description: RegattaCourse of the RegattaCourseRace
 *                $ref: '#/components/schemas/RegattaCourse'
 */

// Helpers
import moment from 'moment';

// Types
import type {
  $Model,
  $ModelClass,
  $ModelClasses,
} from '.';
import type {
  $Field,
} from '../types';

type $VirtualParams = {
  Ongoing: boolean;
};

export type $RelationIds = {
  RegattaCourseId: string;
};

export type $Params = {
  EndDate: Date;
  Name: string;
  Public: boolean;
  StartDate: Date;
  UpadateInterval: number;
};

export type $CreateParams = $Params & $RelationIds;

export type $UpdateParams = {
  id: string;
} & Partial<$CreateParams>;

export type $UpsertParams = $CreateParams & {
  id?: string;
};

export type $QueryParams = {
  id?: null | string;
} & Partial<$CreateParams>;

export type $Associations = {
  RegattaCourse?: $Model<'RegattaCourse'>;
};

export type $ModelParams = $Associations & $CreateParams & $VirtualParams & {
  id: string;
};

type $Fields = $Params & $VirtualParams & {
  id: string;
};

const fields = (
  Sequelize,
): Record<keyof $Fields, $Field> => ({
  EndDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  id: {
    allowNull: false,
    defaultValue: Sequelize.UUIDV4,
    primaryKey: true,
    type: Sequelize.UUID,
    unique: true,
  },
  Name: {
    allowNull: false,
    type: Sequelize.STRING,
    validate: {
      notEmpty: true,
    },
  },
  Ongoing: {
    get(): boolean {
      const startDate: Date = this.get('StartDate');
      const endDate: Date = this.get('EndDate');

      const now: moment.Moment = moment();

      return now.isBetween(
        startDate,
        endDate,
      );
    },

    type: new Sequelize.VIRTUAL(
      Sequelize.Boolean,
      [
        'EndDate',
        'StartDate',
      ],
    ),
  },
  Public: {
    allowNull: false,
    defaultValue: false,
    type: Sequelize.BOOLEAN,
  },
  StartDate: {
    allowNull: false,
    type: Sequelize.DATE,
    validate: {
      notEmpty: true,
    },
  },
  UpadateInterval: {
    allowNull: false,
    type: Sequelize.INTEGER.UNSIGNED,
    validate: {
      notEmpty: true,
    },
  },
});

export default ((
  Sequelize,
  stack,
): $ModelClass<'RegattaCourseRace'> => {
  const RegattaCourseRace = stack.define(
    'RegattaCourseRace',
    fields(Sequelize),
    {
      tableName: 'RegattaCourseRaces',
    },
  );

  RegattaCourseRace.associate = (models: $ModelClasses) => {
    models.RegattaCourseRace.belongsTo(
      models.RegattaCourse,
      {
        as: 'RegattaCourse',
        foreignKey: 'RegattaCourseId',
      },
    );
  };

  RegattaCourseRace.addScope(
    'defaultScope',
    {
      order: [['EndDate', 'ASC']],
    },
  );

  return RegattaCourseRace;
});
